import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi, uploadApi } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if token exists and validate it
    const checkAuth = async () => {
      const token = localStorage.getItem('hyperdriveToken');
      const tokenExpiry = localStorage.getItem('hyperdriveTokenExpiry');
      const rememberMe = localStorage.getItem('hyperdriveRememberMe');
      
      console.log('Auth check - Token exists:', !!token, 'Expiry:', tokenExpiry, 'RememberMe:', rememberMe);
      
      // Check if token has expired based on our stored expiry
      if (tokenExpiry) {
        const expiryTime = parseInt(tokenExpiry);
        const now = new Date().getTime();
        const isExpired = now > expiryTime;
        console.log('Token expiry check - Now:', now, 'Expiry:', expiryTime, 'Expired:', isExpired);
        
        if (isExpired) {
          // Token expired, clear it
          console.log('Token expired, clearing auth data');
          localStorage.removeItem('hyperdriveToken');
          localStorage.removeItem('hyperdriveTokenExpiry');
          localStorage.removeItem('hyperdriveRememberMe');
          setIsAuthenticated(false);
          setUser(null);
          setLoading(false);
          return;
        }
      }
      
      if (token) {
        try {
          console.log('Validating token with server...');
          const userData = await authApi.getMe();
          console.log('Token valid, user:', userData.email);
          setUser(userData);
          setIsAuthenticated(true);
        } catch (error) {
          // Token is invalid, clear it
          console.error('Token validation failed:', error.message);
          localStorage.removeItem('hyperdriveToken');
          localStorage.removeItem('hyperdriveTokenExpiry');
          localStorage.removeItem('hyperdriveRememberMe');
          setIsAuthenticated(false);
          setUser(null);
        }
      } else {
        console.log('No token found in localStorage');
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email, password, rememberMe = false) => {
    try {
      console.log('Login attempt with rememberMe:', rememberMe);
      const { token, user: userData } = await authApi.login(email, password, rememberMe);
      
      // Store token
      localStorage.setItem('hyperdriveToken', token);
      
      // Set token expiration based on "Remember Me" option
      if (rememberMe) {
        // 30 days expiration
        const expiryTime = new Date().getTime() + (30 * 24 * 60 * 60 * 1000);
        localStorage.setItem('hyperdriveTokenExpiry', expiryTime.toString());
        localStorage.setItem('hyperdriveRememberMe', 'true');
        console.log('Token set with 30-day expiry:', new Date(expiryTime).toISOString());
      } else {
        // Session only - 1 day for non-remembered sessions
        const expiryTime = new Date().getTime() + (24 * 60 * 60 * 1000);
        localStorage.setItem('hyperdriveTokenExpiry', expiryTime.toString());
        localStorage.removeItem('hyperdriveRememberMe');
        console.log('Token set with 1-day expiry:', new Date(expiryTime).toISOString());
      }
      
      // Verify storage worked
      const storedToken = localStorage.getItem('hyperdriveToken');
      const storedExpiry = localStorage.getItem('hyperdriveTokenExpiry');
      console.log('Stored token exists:', !!storedToken, 'Stored expiry:', storedExpiry);
      
      setUser(userData);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  const register = async (name, email, password) => {
    try {
      const { token, user: userData } = await authApi.register(name, email, password);
      localStorage.setItem('hyperdriveToken', token);
      setUser(userData);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateUser = async (updates) => {
    try {
      // If updating profile picture with a file
      if (updates.profilePictureFile) {
        const uploadResult = await uploadApi.uploadImage(updates.profilePictureFile);
        // Save only the relative path to database (not full URL)
        updates.profile_picture = uploadResult.url;
        delete updates.profilePictureFile;
      }
      
      // Convert camelCase to snake_case for API
      const apiUpdates = {};
      if (updates.name !== undefined) apiUpdates.name = updates.name;
      if (updates.profile_picture !== undefined) apiUpdates.profile_picture = updates.profile_picture;
      if (updates.profilePicture !== undefined) apiUpdates.profile_picture = updates.profilePicture;
      if (updates.tradingViewUsername !== undefined) apiUpdates.trading_view_username = updates.tradingViewUsername;
      if (updates.trading_view_username !== undefined) apiUpdates.trading_view_username = updates.trading_view_username;
      if (updates.birthday !== undefined) apiUpdates.birthday = updates.birthday;
      
      console.log('Updating user with:', apiUpdates);
      const updatedUser = await authApi.updateMe(apiUpdates);
      console.log('User updated successfully:', updatedUser);
      setUser(updatedUser);
      return { success: true };
    } catch (error) {
      console.error('Update user error:', error);
      // Return more detailed error info
      return { 
        success: false, 
        error: error.message || 'Failed to update profile. Please try again.' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('hyperdriveToken');
    localStorage.removeItem('hyperdriveTokenExpiry');
    localStorage.removeItem('hyperdriveRememberMe');
    setUser(null);
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        background: '#0f0f0f',
        color: '#fff'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, loading, login, register, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};
